import { useSuspenseQuery } from '@apollo/client'
import { useNavigate, useParams } from '@faceup/router'
import { useComponentDidMount, usePromiseSuspense } from '@faceup/ui'
import { type PropsWithChildren, Suspense, useContext, useEffect } from 'react'
import { graphql } from '../__generated__'
import { FullScreenLoader } from '../Components/FullScreenLoader'
import { LayoutContext } from '../Contexts/LayoutContext'
import { UserContext } from '../Contexts/UserContext'
import useRegion from '../utils/useRegion'

const query = graphql(`
  query PublicCompanyByShortIdQuery($id: GraphQLString!) {
    publicReportSourceByShortId(id: $id) {
      password
      reportSource {
        id
        sourceType
        institution {
          id
        }
      }
    }
  }
`)

const _AwaitReportSource = ({ children }: PropsWithChildren) => {
  const { shortId } = useParams<'shortId'>() as { shortId: string }
  const { setReportSource, reportSource } = useContext(UserContext)
  const navigate = useNavigate()
  const { discoverByShortlink } = useRegion()
  const discovery = usePromiseSuspense(discoverByShortlink, [shortId])
  const { client } = useContext(LayoutContext)
  const isApp = client === 'app'

  const { data } = useSuspenseQuery(query, {
    variables: {
      id: shortId,
    },
  })
  useComponentDidMount(() => {
    if (isApp) {
      // In mobile app we handle report source data from localstorage
      // This caused bug when unlogging
      if (!reportSource) {
        navigate(routes => routes.home())
      }
      return
    }
    if (!discovery) {
      console.error('No discovery')
      navigate(routes => routes.home())
      return
    }
    if (!data.publicReportSourceByShortId) {
      console.error('No publicReportSourceByShortId')
      navigate(routes => routes.home())
      return
    }
    const sourceType = data.publicReportSourceByShortId.reportSource.sourceType
    const password = data.publicReportSourceByShortId.password
    if (discovery.reportSourceId && discovery.type) {
      // Must be in hook because setState in render
      setReportSource({
        channelType: sourceType,
        defaultFormId: discovery.reportSourceId,
        institutionId: data.publicReportSourceByShortId.reportSource.institution.id,
        type: discovery.type,
        shortLink: shortId,
        ...(password && { password }),
      })
    }
  })
  return <>{children}</>
}

export const AwaitReportSource = ({ children }: PropsWithChildren) => {
  const { reportSource } = useContext(UserContext)
  const { shortId } = useParams<'shortId'>()
  const navigate = useNavigate()

  // IDK if this can happen, left here for defensive code
  useEffect(() => {
    if (!reportSource && !shortId) {
      console.error('No shortId in AwaitReportSource')
      navigate(routes => routes.home())
      return
    }
  }, [shortId, navigate, reportSource])

  /*
   * When reportSource is set in `_AwaitReportSource`,
   * `AppRouter` is rerendered and suspense inside `_AwaitReportSource` goes into infinite loop
   * So once reportSource is set, we skip this initial configuration
   */
  if (reportSource) {
    return children
  }

  return (
    <Suspense fallback={<FullScreenLoader />}>
      <_AwaitReportSource>{children}</_AwaitReportSource>
    </Suspense>
  )
}
